import { KEY_ROWS, type KeyState } from "./keystate";

interface KeyboardProps {
  states: Map<string, KeyState>;
  onKey: (key: string) => void;
  disabled: boolean;
  /** Key layout — defaults to the latin wordle rows. */
  rows?: string[][];
  /** Display overrides (raw key → label), e.g. "*" → "×". */
  labels?: Record<string, string>;
  /** "flap" = dark board keys matching the split-flap board (Wordle); "notebook" = paper/slate keys (Словко). */
  variant?: "classic" | "flap" | "notebook";
}

const STATE_CLASS: Record<KeyState, string> = {
  g: "bg-tile-correct text-white",
  y: "bg-tile-present text-white",
  b: "bg-tile-absent text-white/80",
};

/** Same palette as `.flap-board` — the board is dark in both Telegram themes, so are its keys. */
const FLAP_STATE_CLASS: Record<KeyState, string> = {
  g: "bg-[#46a257] text-white",
  y: "bg-[#dcaa35] text-[#1e1606]",
  b: "bg-[#17181b] text-[#5f6168]",
};
const FLAP_BASE = "bg-[#2b2c30] text-[#f3efe4] shadow-[inset_0_-3px_0_rgba(0,0,0,0.45)]";

export function Keyboard({ states, onKey, disabled, rows = KEY_ROWS, labels, variant = "classic" }: KeyboardProps) {
  const flap = variant === "flap";
  const notebook = variant === "notebook";
  return (
    <div className="mx-auto flex w-full max-w-[520px] select-none flex-col gap-[7px] px-[6px]">
      {rows.map((row, i) => (
        <div key={i} className={`flex justify-center gap-[6px] ${rows === KEY_ROWS && i === 1 ? "px-[5%]" : ""}`}>
          {row.map((k) => {
            const wide = k === "back";
            const st = states.get(k);
            // Notebook keys are styled by notebook.css through data-s (they follow the Telegram scheme).
            const colour = notebook
              ? "nb-key"
              : st
                ? (flap ? FLAP_STATE_CLASS : STATE_CLASS)[st]
                : flap
                  ? FLAP_BASE
                  : "bg-key-base text-key-text";
            return (
              <button
                key={k}
                type="button"
                disabled={disabled}
                onPointerDown={(e) => {
                  e.preventDefault();
                  onKey(k);
                }}
                onContextMenu={(e) => e.preventDefault()}
                className={`key flex h-[56px] items-center justify-center rounded-[7px] font-bold uppercase transition-transform duration-75 active:scale-95 active:brightness-90 disabled:opacity-60 ${wide ? "flex-[1.6]" : "flex-1 text-[18px]"} ${colour}`}
                aria-label={k}
                data-s={notebook ? (st ?? "") : undefined}
              >
                {k === "back" ? (
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z" />
                    <line x1="18" y1="9" x2="12" y2="15" />
                    <line x1="12" y1="9" x2="18" y2="15" />
                  </svg>
                ) : (
                  (labels?.[k] ?? k)
                )}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}
